import { useMemo } from "react";
import { formatCurrency } from "@/lib/utils";
import { Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Line, ComposedChart } from "recharts";
import type { AffiliateEarningsDataPoint } from "../types";
import {
  type DateRangeValue,
  isCustomRange,
} from "@/components/ui/date-range-selector";
import { format } from "date-fns";

type AffiliateEarningsChartProps = {
  data: AffiliateEarningsDataPoint[];
  isLoading: boolean;
  dateRange: DateRangeValue;
};

// Helper function to get period label
function getPeriodLabel(dateRange: DateRangeValue): string {
  if (isCustomRange(dateRange)) {
    return `${format(dateRange.from, "MMM d")} - ${format(dateRange.to, "MMM d, yyyy")}`;
  }
  
  const labels: Record<string, string> = {
    today: "Today",
    yesterday: "Yesterday",
    this_week: "This Week",
    last_week: "Last Week",
    this_month: "This Month",
    last_month: "Last Month",
    last_7_days: "Last 7 Days",
    last_30_days: "Last 30 Days",
    last_90_days: "Last 90 Days",
    ytd: "Year to Date",
    all: "All Time",
  };
  return labels[dateRange] || "Last 30 Days";
}

export const AffiliateEarningsChart = ({ data, isLoading, dateRange }: AffiliateEarningsChartProps) => {
  const periodLabel = getPeriodLabel(dateRange);
  
  const chartData = useMemo(() => {
    return data.map((d) => ({
      ...d,
      dateLabel: new Date(d.date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
    }));
  }, [data]);

  const totalEarnings = useMemo(() => {
    return data.reduce((sum, d) => sum + d.total, 0);
  }, [data]);

  if (isLoading) {
    return (
      <div className="rounded-xl bg-card p-4 md:p-6 border border-border/50 shadow-sm dark:shadow-none">
        <div className="animate-pulse">
          <div className="h-5 w-32 bg-muted rounded mb-4" />
          <div className="h-[200px] bg-muted/50 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-card p-4 md:p-6 border border-border/50 shadow-sm dark:shadow-none h-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold">Earnings Over Time</h3>
          <p className="text-xs text-muted-foreground">{periodLabel}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Period Total</p>
          <p className="text-lg font-bold text-emerald-500">
            ${formatCurrency(totalEarnings, 2)}
          </p>
        </div>
      </div>

      <div className="h-[220px] md:h-[260px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorTradingFees" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorPerformanceBonus" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="oklch(var(--border))" />
            <XAxis
              dataKey="dateLabel"
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              tickLine={false}
              axisLine={false}
              stroke="#9ca3af"
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              tickLine={false}
              axisLine={false}
              stroke="#9ca3af"
              tickFormatter={(value) => `$${value}`}
              width={50}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                return (
                  <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
                    <p className="text-xs font-medium mb-2">{label}</p>
                    {payload.map((entry, index) => (
                      <div key={index} className="flex items-center gap-2 text-xs">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: entry.color }}
                        />
                        <span className="text-muted-foreground">
                          {entry.name === "tradingFees" 
                            ? "Trading Fees" 
                            : entry.name === "performanceBonus" 
                            ? "Performance Bonus" 
                            : "Total Earnings"}:
                        </span>
                        <span className="font-medium">
                          ${formatCurrency(entry.value as number, 2)}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              }}
            />
            <Area
              type="monotone"
              dataKey="tradingFees"
              stackId="1"
              stroke="#10b981"
              fill="url(#colorTradingFees)"
              strokeWidth={2}
            />
            <Area
              type="monotone"
              dataKey="performanceBonus"
              stackId="1"
              stroke="#f59e0b"
              fill="url(#colorPerformanceBonus)"
              strokeWidth={2}
            />
            <Line
              type="monotone"
              dataKey="total"
              stroke="#fbbf24"
              strokeWidth={2}
              dot={false}
              strokeDasharray="4 2"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 md:gap-6 mt-4 pt-4 border-t border-border/50 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-emerald-500" />
          <span className="text-xs text-muted-foreground">Trading Fees</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-amber-500" />
          <span className="text-xs text-muted-foreground">Performance Bonus</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-0.5 bg-amber-400" style={{ borderStyle: 'dashed' }} />
          <span className="text-xs text-muted-foreground">Total Earnings</span>
        </div>
      </div>
    </div>
  );
};
